import Input from "@/app/components/ui/Inputs/Input";
import InputWithFileUpload from "@/app/components/ui/Inputs/InputWithFileUpload";
import RadioGroupComponent from "@/app/components/ui/RadioGroup";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useUtils } from "@/app/hooks/useUtils";
import { has } from "lodash";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { IAddVendorMSMEDetailsFormFields } from "..";

interface VendorMSMEDetailsFormComponentProps {
  msmeDetailsFormHook: UseFormReturn<IAddVendorMSMEDetailsFormFields>;
  isForEdit: boolean;
}

const VendorMSMEDetailsFormComponent: React.FC<VendorMSMEDetailsFormComponentProps> = ({
  msmeDetailsFormHook: formHook,
  isForEdit,
}) => {
  const { forMsmeValidationValidation } = useFormValidations();
  const { base64ToValidURL } = useUtils();
  useEffect(() => {
    formHook.setValue("registered_status", "yes");
  }, []);
  return (
    <form className="mt-4 sm:mt-8 bg-white rounded-md">
      <p className="text-md font-semibold">MSME Details</p>
      <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
        <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
          <span className="font-normal text-black text-xs">Registered ?</span>
        </div>
        <RadioGroupComponent
          alignment="horizontal"
          errorMessages={[]}
          fieldError={formHook.formState.errors.registered_status}
          groupOptions={[
            {
              id: "yes",
              title: "Yes",
            },
            {
              id: "no",
              title: "No",
            },
          ]}
          onRadioChange={(optionId) => {
            formHook.setValue("registered_status", optionId);
            formHook.setValue("msme_number", null);
          }}
          register={formHook.register("registered_status")}
          defaultSelectedId={formHook.getValues("registered_status")}
        />
      </div>
      <div className="">
        {formHook.watch("registered_status") === "yes" && (
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">MSME Number</span>
            </div>
            <Input
              fieldError={formHook.formState.errors.msme_number ?? {}}
              register={formHook?.register("msme_number", {
                required: false,
                ...forMsmeValidationValidation.validations,
                onBlur: () => formHook.trigger(`msme_number`),
              })}
              placeholder="MSME Number e.g. UDA-92323"
              errorMessages={[
                forMsmeValidationValidation.errors,
                { type: "required", message: "MSME Number Is Required" },
              ]}
              className="border-slate-400 rounded-md w-full sm:w-[300px] h-9 text-sm"
              inputClassName="h-[38px] rounded-md"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                formHook?.setValue("msme_number", e.target?.value);
                formHook.clearErrors("msme_number");
              }}
            />
          </div>
        )}
        {formHook.watch("registered_status") === "yes" && (
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">MSME Certificate</span>
            </div>
            <div className="w-full sm:w-2/6 lg:w-1/4">
              <InputWithFileUpload
                variant="DRAG_AND_DROP"
                fieldError={formHook.formState.errors.declaration_attachment_file ?? {}}
                register={formHook?.register("declaration_attachment_file", {
                  required: false,
                  onBlur: () => formHook.trigger(`declaration_attachment_file`),
                  validate: {
                    maxSize: (files: FileList) => {
                      return (
                        Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                        "Each file must be smaller than 5MB"
                      );
                    },
                    fileType: (files: FileList) => {
                      const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                      return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                    },
                  },
                })}
                onClear={() => {
                  formHook.resetField(`declaration_attachment_file`);
                }}
                files={formHook.watch("declaration_attachment_file")}
                type="file"
                id="declaration_attachment_file"
                errorMessages={[{ type: "required", message: "Media Required" }]}
                dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
              />
              <div className="flex items-center gap-2 my-3">
                <div className="flex items-center gap-2 my-3">
                  {formHook?.watch("attachment") && has(formHook?.watch("attachment"), "base_64_string") ? (
                    <img
                      src={base64ToValidURL(
                        formHook?.watch("attachment").base_64_string,
                        formHook?.watch("attachment").file_extension,
                      )}
                      alt=""
                      className="w-full sm:w-1/2 h-full"
                    />
                  ) : (
                    <img src={formHook?.watch("attachment")?.document_url} alt="" className="w-full sm:w-1/2 h-full" />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {formHook.watch("registered_status") === "no" && (
          <div className="flex flex-col gap-1.5 sm:flex-row sm:justify-start sm:items-baseline mt-5">
            <div className="flex justify-start items-center gap-1 w-full sm:w-[250px] h-auto sm:h-[38px]">
              <span className="font-normal text-black text-xs">Declaration</span>
              <span className="font-normal text-rose-600 text-xs">*</span>
            </div>
            <div className="w-full sm:w-2/6 lg:w-1/4">
              <InputWithFileUpload
                variant="SIMPLE"
                fieldError={formHook.formState.errors.declaration_attachment_file ?? {}}
                register={formHook?.register("declaration_attachment_file", {
                  required: !formHook.getValues("declaration_attachment_file"),
                  onBlur: () => formHook.trigger(`declaration_attachment_file`),
                  validate: {
                    maxSize: (files: FileList) => {
                      return (
                        Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                        "Each file must be smaller than 5MB"
                      );
                    },
                    fileType: (files: FileList) => {
                      const validTypes = ["image/png", "image/jpeg", "application/pdf"];
                      return Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type";
                    },
                  },
                })}
                onClear={() => {
                  formHook.resetField(`declaration_attachment_file`);
                }}
                files={formHook.watch("declaration_attachment_file")}
                type="file"
                className="w-full sm:w-1/3"
                id="declaration_attachment_file"
                errorMessages={[{ type: "required", message: "Media Required" }]}
                dragAndDropDescription={{ allowedFormats: ["PDF", "JPEG", "PNG"], maxFileSize: "5MB" }}
              />
              <div className="flex items-center gap-2 my-3">
                {/* {imageUrls[3] && (
                  <div className="flex items-center gap-8 px-4 py-2">
                    <img src={imageUrls[3]} alt="PAN attachment" className="w-auto h-[100px] object-cover" />
                  </div>
                )} */}
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default VendorMSMEDetailsFormComponent;
